import { overlayRoot } from './columnScale';
import { dieArt } from './diceArt';
import { registerEscape } from './keyboard';
import { APP_VERSION } from './screens/devNotes';
import type { ScreenCtx } from './types';

/**
 * **Crédits** (phase 5, A78, A82) : déroulés d'eux-mêmes par-dessus l'écran de
 * victoire la première fois (`rollCredits`), rejouables depuis son bouton
 * « Crédits », et revoyables sans animation dans l'onglet « Crédits » des Notes
 * de dev (`creditsHtml`).
 *
 * Texte d'A78 : « Game design : Nanou · Développement : Planplan & Clauclau ·
 * Produit par Calypso gaming » — les points médians séparent les trois lignes,
 * aucun mot ne change.
 */

/** Les trois lignes exactes d'A78, dans l'ordre. */
export const CREDIT_LINES: readonly { role: string; name: string }[] = [
  { role: 'Game design :', name: 'Nanou' },
  { role: 'Développement :', name: 'Planplan & Clauclau' },
  { role: 'Produit par', name: 'Calypso gaming' },
];

/** Durée du défilement (≈ 12 s, de bas en haut). */
export const CREDITS_ROLL_MS = 12_000;
/** Mouvement réduit : les crédits restent posés, immobiles, 4 s. */
export const CREDITS_STILL_MS = 4_000;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Markup statique F1 des crédits : emblème, titre, filet d'or, les trois lignes
 * (rôle puis nom), remerciement et version. Une ligne a pour texte exact
 * « rôle nom » (« Game design : Nanou »).
 */
export function creditsHtml(): string {
  const lines = CREDIT_LINES.map(
    (l) =>
      `<li class="credits-line"><span class="credits-role">${escapeHtml(l.role)}</span> <span class="credits-name">${escapeHtml(l.name)}</span></li>`,
  ).join('');
  return `<div class="credits" data-role="credits">
  <div class="credits-emblem">${dieArt(20, 'legendary', { size: 72, stroke: 2.4 })}</div>
  <h2 class="credits-title">DiceOrDie&nbsp;!</h2>
  <div class="f-rule" aria-hidden="true"></div>
  <ul class="credits-lines">${lines}</ul>
  <p class="credits-thanks">Merci d'avoir joué.</p>
  <p class="credits-version">v${APP_VERSION}</p>
</div>`;
}

export interface RollCreditsOptions {
  /** Appelé **une fois**, à la fin du défilement ou quand le joueur le passe. */
  onDone?: () => void;
}

function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Déroule les crédits par-dessus l'écran courant : voile `f-scrim` dans
 * `overlayRoot()`, markup qui monte de bas en haut en ≈ 12 s (`@keyframes`,
 * `transform`) ; mouvement réduit → affichage fixe 4 s. Un toucher n'importe
 * où ou `Échap` termine. `onDone` est appelé une seule fois.
 *
 * Renvoie l'**annulation** (démontage de l'écran) : le voile disparaît sans
 * appeler `onDone`.
 */
export function rollCredits(_ctx: ScreenCtx | null, opts: RollCreditsOptions = {}): () => void {
  const still = prefersReducedMotion();
  const overlay = document.createElement('div');
  overlay.className = `f-scrim credits-overlay${still ? ' credits-overlay--still' : ''}`;
  overlay.setAttribute('data-role', 'credits-overlay');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Crédits');
  overlay.style.setProperty('--credits-dur', `${CREDITS_ROLL_MS}ms`);
  overlay.innerHTML = `
    <div class="credits-track"><div class="credits-roll">${creditsHtml()}</div></div>
    <p class="credits-skip">Touchez pour passer</p>`;

  let finished = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unregisterEscape = (): void => {};
  const finish = (notify: boolean): void => {
    if (finished) return;
    finished = true;
    if (timer !== undefined) clearTimeout(timer);
    unregisterEscape();
    overlay.removeEventListener('click', onTouch);
    overlay.remove();
    if (notify) opts.onDone?.();
  };
  const onTouch = (): void => finish(true);

  overlay.addEventListener('click', onTouch);
  overlayRoot().appendChild(overlay);
  unregisterEscape = registerEscape(overlay, onTouch);
  timer = setTimeout(onTouch, still ? CREDITS_STILL_MS : CREDITS_ROLL_MS);
  return () => finish(false);
}
